import { RichTextParser } from '@PetSureAustralia/petsure-react-ui-library/distLib/helpers';
import { toCamelCase } from './formatString';
import { TAXONOMY_CODENAME } from '../constants/cmsCodeName';
import {
  isAllowedByTaxonomyChecklistField,
  isAllowedByTaxonomyDropdownField,
  resolveModularElementValue,
  resolveModularTooltipElementValue,
  resolveGeneralTooltipElementValue
} from '../helpers/kontentAiHelper';
import {
  replaceDisplayTagPlaceholders,
  resolveTooltipContent,
  replaceSummaryObjects,
  replacePanelSummaryObjects
} from './placeholderUtils';

export const transformFooterDisclaimersData = (apiData) => {
  if (!apiData?.elements) return {};
  const [elementKey] = Object.keys(apiData.elements);
  return Object.values(apiData.elements).reduce((acc, element) => {
    if (element?.value) {
      acc[elementKey] = element.value;
    }
    return acc;
  }, {});
};

export const transformMarketingTilesData = (apiData = []) => {
  if (!Array.isArray(apiData)) return [];

  return apiData.map((item) => {
    const { system, elements } = item || {};
    const [asset] = elements?.image?.value ?? [];
    return {
      id: system?.id,
      codename: system?.codename,
      heading: elements?.heading?.value ?? '',
      image: asset
        ? {
            url: asset.url,
            alt: asset.name || elements?.heading?.value || '',
            width: asset.width,
            height: asset.height
          }
        : null,
      blobImage: elements?.blob_image?.value ?? '',
      brandPartners: elements?.brand_partners?.value?.map((t) => t.codename) ?? [],
      linkUrl: elements?.link_url?.value ?? ''
    };
  });
};

export const transformFaqsData = (apiData) => {
  const transformed = {};
  if (!apiData?.elements) return transformed;
  Object.keys(apiData.elements).forEach((key) => {
    const element = apiData.elements[key];
    if (element.type !== 'modular_content' || !element.linkedItems || element.linkedItems.length === 0) return;
    const normalizedKey = element.name;
    const questions = element.linkedItems.map((item) => ({
      id: item.system.id,
      question: item.elements.question?.value || 'No question available',
      answer: item.elements.answer?.value || 'No answer available'
    }));
    transformed[normalizedKey] = {
      title: element.name,
      questions
    };
  });
  return transformed;
};

export const transformCmsTags = (linkedItems) => {
  if (!Array.isArray(linkedItems) || linkedItems.length === 0) return {};

  const { elements } = linkedItems[0];

  return Object.keys(elements)?.reduce((acc, key) => {
    const { value } = elements[key];
    if (Array.isArray(value)) {
      acc[key] = value?.length > 0 && value[0]?.name ? value[0].name : null;
    } else {
      acc[key] = value;
    }
    return acc;
  }, {});
};

export const transformWhatsCoveredData = (raw, planType, policyCover) => {
  const allKeys = Object.keys(raw.elements);
  const contentKeys = allKeys.filter((k) => k.endsWith('__content'));
  const sections = contentKeys.map((key) => {
    const prefix = key.replace(/__content$/, '');
    const sectionId = toCamelCase(prefix);
    const block = raw.elements[key];
    const items = block.linkedItems.map((item) => {
      const { codename } = item.system;
      const fields = [];

      if (
        isAllowedByTaxonomyChecklistField(item, planType, TAXONOMY_CODENAME.PLAN_TYPE) &&
        isAllowedByTaxonomyDropdownField(item, policyCover, TAXONOMY_CODENAME.POLICY_COVER)
      ) {
        if (item.elements.heading) {
          fields.push({ id: `${codename}__heading`, ...item.elements.heading });
        }
        if (item.elements.body_content) {
          fields.push({ id: `${codename}__body`, ...item.elements.body_content });
        }

        if (item.elements.policy_benefits) {
          fields.push({ id: `${codename}__policy_benefits`, ...item.elements.policy_benefits });
        }
      }

      // Merge in content_block_variants fields into the same item
      if (item.elements.content_block_variants) {
        const variantsBlock = item.elements.content_block_variants;

        variantsBlock.linkedItems.forEach((variant) => {
          const variantCodename = variant.system.codename;

          if (
            isAllowedByTaxonomyChecklistField(variant, planType, TAXONOMY_CODENAME.PLAN_TYPE) &&
            isAllowedByTaxonomyDropdownField(variant, policyCover, TAXONOMY_CODENAME.POLICY_COVER)
          ) {
            if (variant.elements.heading) {
              fields.push({ id: `${variantCodename}__heading`, ...variant.elements.heading });
            }

            if (variant.elements.body_content) {
              fields.push({ id: `${variantCodename}__body`, ...variant.elements.body_content });
            }
          }
        });
      }

      return { codename, fields };
    });

    return { sectionId, items };
  });

  const flatFields = sections.flatMap(({ sectionId, items }) =>
    items.flatMap(({ fields }) =>
      fields.map((field) => ({
        section: sectionId,
        fullKey: field.id,
        field
      }))
    )
  );

  return { flatFields };
};

export const transformTooltipsData = (apiData) => {
  if (!Array.isArray(apiData)) return [];
  return apiData.map((item) => ({
    id: item.system.id,
    codename: item.system.codename,
    name: item.system.name,
    language: item.system.language,
    type: item.system.type,
    text: item.elements.tool_tip_text.value
  }));
};

export const transformBannerContent = (response) => {
  const { item, linkedItems } = response.data;
  if (!item || !linkedItems) return { messageVariants: [], buttonVariants: [] };

  const messageVariants = item?.elements?.message_variants?.value?.map((codename) => linkedItems[codename]);

  const buttonVariants = item?.elements?.button_variants?.value?.map((codename) => linkedItems[codename]);

  return { messageVariants, buttonVariants };
};

export const transformGeneralModularContent = (raw, response, element = []) => {
  const content = {};
  element.forEach((el) => {
    content[el] = resolveModularElementValue(raw, response, el);
  });

  return { content };
};

export const transformGeneralModularTooltip = (response, element = []) => {
  const content = {};
  element.forEach((el) => {
    content[el] = resolveModularTooltipElementValue(response, el);
  });

  return { content };
};

export const transformGeneralTooltip = (response, element = []) => {
  const content = {};
  element.forEach((el) => {
    content[el] = resolveGeneralTooltipElementValue(response, el);
  });

  return { content };
};

export const transformCMSRichText = (
  str,
  fallbackOrValues = '',
  valueMapping = {},
  placeholderMapping = {},
  tooltipContent = {},
  tooltipModalBody = {},
  shouldLowercasePlaceholderValues = false
) => {
  const hasFallback = typeof fallbackOrValues === 'string';
  const fallback = hasFallback ? fallbackOrValues : '';
  const rawPlaceholderValues = hasFallback ? valueMapping : fallbackOrValues;
  const placeholderValues = rawPlaceholderValues;
  const placeholderKeyMap = hasFallback ? placeholderMapping : valueMapping;

  if (str === null || str === undefined || str === '') return fallback;

  const resolvedContent = replaceDisplayTagPlaceholders(
    str,
    placeholderValues,
    placeholderKeyMap,
    shouldLowercasePlaceholderValues
  );
  const tooltipResolvedContent = resolveTooltipContent(resolvedContent, tooltipContent, tooltipModalBody);
  return RichTextParser(tooltipResolvedContent);
};

export const transformContent = (
  str,
  fallbackOrValues = '',
  valueMapping = {},
  placeholderMapping = {},
  content = {}
) => {
  const hasFallback = typeof fallbackOrValues === 'string';
  const fallback = hasFallback ? fallbackOrValues : '';
  const placeholderValues = hasFallback ? valueMapping : fallbackOrValues;
  const placeholderKeyMap = hasFallback ? placeholderMapping : valueMapping;
  if (str === null || str === undefined || str === '') return fallback;
  const objectResolvedContent = replaceSummaryObjects(str, placeholderValues, content);
  const resolvedContent = replaceDisplayTagPlaceholders(objectResolvedContent, placeholderValues, placeholderKeyMap);
  return RichTextParser(resolvedContent);
};

export const transformPanelSummaryContent = (
  fallbackOrValues = '',
  valueMapping = {},
  placeholderMapping = {},
  content = {}
) => {
  const hasFallback = typeof fallbackOrValues === 'string';
  const fallback = hasFallback ? fallbackOrValues : '';
  const placeholderValues = hasFallback ? valueMapping : fallbackOrValues;
  const placeholderKeyMap = hasFallback ? placeholderMapping : valueMapping;
  const cmsContent = replacePanelSummaryObjects(placeholderValues, content);
  const baseContent = cmsContent || fallback;
  const resolvedContent = replaceDisplayTagPlaceholders(baseContent, placeholderValues, placeholderKeyMap);
  return RichTextParser(resolvedContent);
};
